/**
 * Application shell — top bar, footer, methodology summary, "origem
 * científica" panel, and the historical-evolution section (design D14 topbar
 * follow-up; av-methodology-matrix-ui spec). Recreates the historical
 * prototype's `header#topo` organization (logo, Novo/Imprimir/Ajuda/Sobre,
 * contextual instructions box) — modernized visually, same functional
 * organization — per the author's explicit instruction to treat
 * `docs/art-direction/metodologia-av/` as the primary interactional
 * reference, not just a historical footnote. Visual identity informally
 * reuses labs/ tokens (design D10) without copying its specific components.
 */

import { manifest, methodology, references } from "../domain/data-source";
import { listVersions } from "../domain/manifest";

export interface TopBarActions {
  onNovo: () => void;
  onImprimir: () => void;
  onAjuda: () => void;
  onSobre: () => void;
}

const LOGO_SVG = `
<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="28" height="28" rx="6" fill="var(--accent)"/>
  <path d="M8 15v-2a6 6 0 0 1 12 0v2" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round"/>
  <rect x="6" y="14" width="4" height="6" rx="2" fill="#fff"/>
  <rect x="18" y="14" width="4" height="6" rx="2" fill="#fff"/>
</svg>`;

/**
 * Historical: `header#topo` had `<img class="imglogo">` + `<menu>` (Novo,
 * Imprimir, Ajuda, Sobre) + `<details>` (contextual instructions). Reproduced
 * here with the same functional organization, contemporary styling.
 */
export function renderTopBar(container: HTMLElement, actions: TopBarActions): void {
  const bar = document.createElement("header");
  bar.className = "site-header";
  bar.id = "topo";
  bar.innerHTML = `
    <div class="container topbar-inner">
      <a class="logo" href="#top">
        ${LOGO_SVG}
        <span>Metodologia AV</span>
      </a>
      <nav class="topbar-menu" aria-label="Ações da ferramenta">
        <button type="button" class="topbar-action" data-action="novo">Novo</button>
        <button type="button" class="topbar-action" data-action="imprimir">Imprimir</button>
        <button type="button" class="topbar-action" data-action="ajuda">Ajuda</button>
        <button type="button" class="topbar-action" data-action="sobre">Sobre</button>
      </nav>
      <p class="topbar-details">
        1 — Clique em um quadrante para inserir o conteúdo da sua página web.<br>
        2 — Selecione um conteúdo e use "+" para associar influências/hibridismos.
      </p>
    </div>
  `;
  container.appendChild(bar);

  bar.querySelector('[data-action="novo"]')?.addEventListener("click", actions.onNovo);
  bar.querySelector('[data-action="imprimir"]')?.addEventListener("click", actions.onImprimir);
  bar.querySelector('[data-action="ajuda"]')?.addEventListener("click", actions.onAjuda);
  bar.querySelector('[data-action="sobre"]')?.addEventListener("click", actions.onSobre);
}

/**
 * Historical: `.infobg` (dimmed overlay) + `.infocont` (content box) +
 * `.fechar` (close "X"), toggled by `MM_showHideLayers`. Reproduced with the
 * same structure (overlay + box + close), a native `<dialog>`-free approach
 * for broad compatibility, closing on click-outside or Escape.
 */
export function showModal(title: string, bodyHtml: string): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", title);
  overlay.innerHTML = `
    <div class="modal-box">
      <button type="button" class="modal-close" aria-label="Fechar">✕</button>
      <h2>${title}</h2>
      <div class="modal-body">${bodyHtml}</div>
    </div>
  `;
  document.body.appendChild(overlay);

  function close(): void {
    overlay.remove();
    document.removeEventListener("keydown", onKeydown);
  }
  function onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") close();
  }

  overlay.querySelector(".modal-close")?.addEventListener("click", close);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener("keydown", onKeydown);

  const closeBtn = overlay.querySelector<HTMLElement>(".modal-close");
  closeBtn?.focus();
}

export function ajudaContentHtml(): string {
  return `
    <p>Este quadro metodológico auxilia o planejamento de conteúdo audiovisual e recursos sonoros
    para websites, a partir da pesquisa de doutorado de Daniela Carvalho Monteiro Ferreira (Unicamp, 2010).</p>
    <ul>
      <li><strong>Criar um conteúdo:</strong> clique em uma célula vazia da matriz e informe o nome.</li>
      <li><strong>Mover um conteúdo:</strong> arraste o quadrado para outra célula (mouse, toque ou caneta),
      ou use as setas do teclado quando o quadrado estiver focado.</li>
      <li><strong>Adicionar uma influência:</strong> selecione um conteúdo e clique no "+" que aparece
      junto a ele; arraste a bolinha resultante até a posição desejada.</li>
      <li><strong>Editar peso:</strong> clique duas vezes no quadrado ou na bolinha.</li>
      <li><strong>Excluir:</strong> selecione (clique) e pressione Delete. Excluir um conteúdo remove
      também suas influências.</li>
      <li><strong>Interpretar o resultado:</strong> o painel abaixo da matriz mostra, nesta ordem, a
      indicação metodológica (posição → características → necessidade de som), os recursos
      tecnológicos sugeridos (histórico de 2010 e atual de 2026, sempre distinguíveis), e o resultado
      de hibridismo calculado a partir das influências do conteúdo selecionado.</li>
    </ul>
  `;
}

export function sobreContentHtml(): string {
  const versions = listVersions()
    .map((v) => `<li><strong>${v.year}</strong> — ${v.label} (${v.status === "current" ? "atual" : "histórico"})</li>`)
    .join("");
  const refs = references.references
    .map((ref) => `<li>${ref.title ?? ref.id}${ref.year ? ` (${ref.year})` : ""}</li>`)
    .join("");
  return `
    <p>${methodology.purpose.summary}</p>
    <h3>Versões</h3>
    <ul>${versions}</ul>
    <h3>Proveniência</h3>
    <ul>${refs}</ul>
  `;
}

/**
 * Text left / audionainternet icon motif right — reproducing labs/'s hero
 * exactly (labs/index.html's .hero/.hero-copy/.hero-icons, same three SVGs)
 * per the author's explicit instruction, on the same navy background
 * (design D20). The icon set — headphone / speaker / microphone — is
 * copied verbatim from labs/, not redrawn, so the motif reads as the same
 * mark across the ecosystem.
 */
export function renderIntro(container: HTMLElement): void {
  const section = document.createElement("section");
  section.id = "top";
  section.innerHTML = `
    <div class="container hero-inner">
      <div class="hero-copy">
        <p class="eyebrow">audionainternet</p>
        <h1>Metodologia AV</h1>
        <p>${escapeHtml(methodology.purpose.summary)}</p>
        <p class="origin-badge">Origem: pesquisa de doutorado de Daniela Carvalho Monteiro Ferreira
        (Unicamp, ${manifest.versions.find((v) => v.year === 2010)?.year}). Revisão contemporânea:
        Metodologia AV ${manifest.currentVersion} (${manifest.currentReview}).</p>
      </div>
      <div class="hero-icons" role="img" aria-label="Ícones representando fones de ouvido, som e microfone, temas centrais da pesquisa em áudio na internet">
        <div class="hero-icon hero-icon--orange">
          <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
            <path d="M16 34 A16 16 0 0 1 48 34" fill="none" stroke="#ffffff" stroke-width="5" stroke-linecap="round" />
            <rect x="12" y="32" width="9" height="16" rx="4.5" fill="#ffffff" />
            <rect x="43" y="32" width="9" height="16" rx="4.5" fill="#ffffff" />
          </svg>
        </div>
        <div class="hero-icon hero-icon--green">
          <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
            <path d="M20 24 L28 24 L38 16 L38 48 L28 40 L20 40 Z" fill="#ffffff" />
            <path d="M44 24 A12 12 0 0 1 44 40" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" />
            <path d="M48 18 A20 20 0 0 1 48 46" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" opacity="0.7" />
          </svg>
        </div>
        <div class="hero-icon hero-icon--teal">
          <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
            <rect x="24" y="10" width="16" height="28" rx="8" fill="#ffffff" />
            <path d="M18 28 A14 14 0 0 0 46 28" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" />
            <line x1="32" y1="42" x2="32" y2="52" stroke="#ffffff" stroke-width="4" stroke-linecap="round" />
            <line x1="22" y1="52" x2="42" y2="52" stroke="#ffffff" stroke-width="4" stroke-linecap="round" />
          </svg>
        </div>
      </div>
    </div>
  `;
  container.appendChild(section);
}

export function renderOriginPanel(container: HTMLElement): void {
  const section = document.createElement("section");
  section.id = "origem";
  const list = references.references
    .map((ref) => {
      const authors = ref.authors ? ref.authors.join("; ") : ref.type === "technical-reference" ? String(ref.organization ?? "") : "";
      const year = ref.year ? ` (${ref.year})` : "";
      const status = (ref as { status?: string }).status;
      return `<li><strong>${escapeHtml(ref.title ?? ref.id)}</strong>${year}${authors ? ` — ${escapeHtml(authors)}` : ""}${status ? ` <span class="provenance-tag pending">${escapeHtml(status)}</span>` : ""}</li>`;
    })
    .join("");
  section.innerHTML = `
    <div class="container">
      <h2>Origem científica</h2>
      <p>A Metodologia AV é um artefato científico. Cada conceito, recurso e recomendação
      apresentado nesta aplicação é rastreável a uma destas fontes.</p>
      <ul class="references-list">${list}</ul>
    </div>
  `;
  container.appendChild(section);
}

export function renderEvolutionSection(container: HTMLElement): void {
  const section = document.createElement("section");
  section.id = "evolucao";
  const cards = listVersions()
    .map(
      (v) => `
      <div class="evolution-card">
        <div class="year">${v.year}</div>
        <h3>${escapeHtml(v.label)}</h3>
        <p>${escapeHtml(v.type)} — <span class="provenance-tag ${v.status === "current" ? "current" : "historical"}">${v.status === "current" ? "atual" : "histórico"}</span></p>
      </div>
    `,
    )
    .join("");
  section.innerHTML = `
    <div class="container">
      <h2>Da Web de 2010 à Web contemporânea</h2>
      <p>Esta seção mostra a evolução registrada da Metodologia AV. O texto editorial completo
      desta narrativa ainda não foi redigido — os dados abaixo vêm diretamente de
      <code>manifest.json</code>, sem afirmações não sustentadas por fonte.</p>
      <div class="evolution-timeline">${cards}</div>
    </div>
  `;
  container.appendChild(section);
}

export function renderFooter(container: HTMLElement): void {
  const footer = document.createElement("footer");
  footer.className = "site-footer";
  footer.innerHTML = `
    <div class="container">
      <p>Metodologia AV — parte do ecossistema Audio na Internet.</p>
      <nav aria-label="Navegação do rodapé">
        <a href="https://labs.audionainternet.com.br">labs.audionainternet.com.br</a>
        <a href="privacy-policy.html">Política de Privacidade</a>
      </nav>
    </div>
  `;
  container.appendChild(footer);
}

/**
 * Below-767px fallback (av-methodology-matrix-ui delta, fix-metodologia-
 * smartphone-warning-placement): rendered here — not as static index.html
 * markup — so it lands at the same DOM position #matriz/#resultado occupy
 * (right after the top bar, before the intro/blue section), instead of
 * after everything #app contains. Visibility stays pure CSS (see
 * styles.css's @media (max-width: 767px) block); this function always
 * creates and appends the element, mirroring renderIntro/renderFooter.
 */
const ATTENTION_ICON_SVG = `
<svg class="mobile-warning-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
  <path d="M12 3 L22 20 L2 20 Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
  <line x1="12" y1="9" x2="12" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <circle cx="12" cy="17" r="1" fill="currentColor" />
</svg>`;

export function renderMobileWarning(container: HTMLElement): void {
  const section = document.createElement("section");
  section.className = "mobile-warning";
  section.setAttribute("role", "status");
  section.innerHTML = `
    <div class="container mobile-warning-inner">
      <p class="mobile-warning-title">${ATTENTION_ICON_SVG}Disponível em telas maiores</p>
      <p class="mobile-warning-text">
        A Metodologia AV é uma ferramenta interativa que funciona melhor em
        tablet ou computador. Acesse por um desses dispositivos para
        planejar seu conteúdo audiovisual.
      </p>
      <p class="mobile-warning-text">
        Agradecemos a visita e esperamos que volte em um tablet ou
        computador para explorar a Metodologia AV com boas descobertas
        criativas.
      </p>
    </div>
  `;
  container.appendChild(section);
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
